// src/hooks/useUrlCrypto.js
import { useMemo } from 'react';

/**
 * URL-safe base64 helpers (RFC 4648 §5)
 */
const toBase64Url = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

const fromBase64Url = (b64url) => {
  const b64 =
    b64url.replace(/-/g, '+').replace(/_/g, '/') +
    '==='.slice((b64url.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

const te = new TextEncoder();
const td = new TextDecoder();

/**
 * Derive an AES-GCM CryptoKey from a passphrase using PBKDF2.
 */
async function deriveKey(passphrase, salt) {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    te.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 150_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * useUrlCrypto(passphrase, opts?)
 * - Output format (base64url of bytes): [version(2)][salt(16)][iv(12)][ciphertext(...)]
 */
export function useUrlCrypto(passphrase, opts = {}) {
  const hasWebCrypto =
    typeof window !== 'undefined' &&
    typeof window.crypto?.subtle !== 'undefined';

  const rng =
    opts.rng ??
    ((len) => {
      const a = new Uint8Array(len);
      crypto.getRandomValues(a);
      return a;
    });

  const versionTag = (opts.versionTag ?? 'v1').slice(0, 2); // 2 bytes

  return useMemo(() => {
    if (!hasWebCrypto) {
      return {
        encrypt: async () => {
          throw new Error(
            'Web Crypto API not available (are you running on the server?)'
          );
        },
        decrypt: async () => {
          throw new Error(
            'Web Crypto API not available (are you running on the server?)'
          );
        },
      };
    }

    const encrypt = async (data) => {
      const plaintext = te.encode(JSON.stringify(data));
      const salt = rng(16);
      const iv = rng(12);

      const key = await deriveKey(passphrase, salt);
      const ctBuf = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        plaintext
      );
      const ct = new Uint8Array(ctBuf);

      const ver = te.encode(versionTag);
      const packed = new Uint8Array(2 + 16 + 12 + ct.length);
      packed.set(ver, 0);
      packed.set(salt, 2);
      packed.set(iv, 18);
      packed.set(ct, 30);

      return toBase64Url(packed);
    };

    const decrypt = async (token) => {
      const packed = fromBase64Url(token);
      if (packed.length < 30) throw new Error('Malformed token');

      const ver = td.decode(packed.slice(0, 2));
      if (ver !== versionTag)
        throw new Error(`Unsupported token version: ${ver}`);

      const salt = packed.slice(2, 18);
      const iv = packed.slice(18, 30);
      const ct = packed.slice(30);

      const key = await deriveKey(passphrase, salt);
      try {
        const ptBuf = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          ct
        );
        return JSON.parse(td.decode(new Uint8Array(ptBuf)));
      } catch {
        throw new Error(
          'Decryption failed (bad passphrase or corrupted token)'
        );
      }
    };

    return { encrypt, decrypt };
  }, [passphrase, hasWebCrypto, rng, versionTag]);
}
